import { useRef, useState } from "react";
import api from "@/admin/api";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2, AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";

export const ImportInstrumentModal = ({ studies, studyId, instruments, onClose, onCreated }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("new_instrument");
  const [targetStudy, setTargetStudy] = useState(studyId || (studies[0]?.study_id ?? ""));
  const [title, setTitle] = useState("");
  const [targetInstrument, setTargetInstrument] = useState(instruments[0]?.instrument_id ?? "");

  const pickFile = (f) => {
    setFile(f);
    setPreview(null);
    if (f && !title) setTitle(f.name.replace(/\.xlsx$/i, ""));
  };

  const runPreview = async () => {
    if (!file) { toast.error("Pilih file .xlsx dulu"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/instruments/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      if (r.data.ok) toast.success("Validasi berhasil — tinjau pratinjau di bawah");
      else toast.error("Validasi gagal — periksa daftar error");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memvalidasi file"); }
    finally { setBusy(false); }
  };

  const createDraft = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      if (mode === "new_instrument") { fd.append("study_id", targetStudy); fd.append("title", title.trim()); }
      else { fd.append("instrument_id", targetInstrument); }
      const r = await api.post("/admin/instruments/import/create", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Draf v${r.data.version.version_no} dibuat dari XLSX`);
      onCreated(r.data.version.version_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat draf"); }
    finally { setBusy(false); }
  };

  const inputCls = "rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";
  const s = preview?.summary || {};

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-10" data-testid="import-modal">
      <div className="w-full max-w-3xl rounded-comiq border bg-comiq-surface-alt shadow-xl" style={{ borderColor: COLORS.line }}>
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: COLORS.line }}>
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} style={{ color: COLORS.orangeInk }} />
            <h2 className="text-lg font-semibold" style={{ color: COLORS.ink }}>Import Instrumen dari XLSX</h2>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-black/5" data-testid="import-close-btn"><X size={18} style={{ color: COLORS.textSoft }} /></button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <p className="text-sm" style={{ color: COLORS.textSoft }}>
            Gunakan workbook ComIQ resmi (sheet: Kuesioner, Opsi Jawaban, Skoring, Penanda Kualitas). Teks, kode, urutan, jangkar, dan aturan skoring dibaca apa adanya. Hasil selalu berupa <strong>draf</strong> — tidak diterbitkan otomatis.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            <button onClick={() => fileRef.current?.click()} className="btn-comiq btn-line" data-testid="import-choose-file-btn"><UploadCloud size={15} /> {file ? "Ganti file" : "Pilih file .xlsx"}</button>
            {file && <span className="text-sm" style={{ color: COLORS.ink }} data-testid="import-filename">{file.name}</span>}
            <button onClick={runPreview} disabled={!file || busy} className="btn-comiq btn-accent disabled:opacity-40" data-testid="import-validate-btn">
              {busy ? <Loader2 size={15} className="animate-spin" /> : null} Validasi &amp; Pratinjau
            </button>
          </div>

          {preview && (
            <div className="space-y-4" data-testid="import-preview">
              {preview.errors?.length > 0 && (
                <div className="rounded-comiq-sm border p-3" style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }} data-testid="import-errors">
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#B42318" }}><AlertTriangle size={15} /> Error validasi ({preview.errors.length})</div>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm" style={{ color: "#B42318" }}>
                    {preview.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </div>
              )}
              {preview.warnings?.length > 0 && (
                <div className="rounded-comiq-sm border p-3" style={{ borderColor: "#FCD34D", background: "#FFFBEB" }} data-testid="import-warnings">
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#B45309" }}><AlertTriangle size={15} /> Peringatan ({preview.warnings.length})</div>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm" style={{ color: "#B45309" }}>
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {preview.ok && (
                <>
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-6" data-testid="import-summary">
                    {[["Item", s.n_items], ["Blok", s.n_sections], ["Opsi", s.n_options], ["Skala", s.n_scales], ["Diskor", s.n_scored], ["Aturan skor", s.n_scoring_rules]].map(([label, val]) => (
                      <div key={label} className="rounded-comiq-sm border bg-white p-3 text-center" style={{ borderColor: COLORS.line }}>
                        <div className="font-mono text-xl font-bold" style={{ color: COLORS.ink }}>{val}</div>
                        <div className="text-xs" style={{ color: COLORS.textSoft }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-comiq-sm border bg-white p-3" style={{ borderColor: COLORS.line }}>
                    <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLORS.textSoft }}>Daftar skor</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(preview.scoring?.layers || {}).map(([k, codes]) => (
                        <span key={k} className="rounded-full border px-2.5 py-1 font-mono text-xs" style={{ borderColor: COLORS.line, color: COLORS.ink }} title={codes.join(", ")}>{k} ({codes.length})</span>
                      ))}
                      {preview.scoring?.index && <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: "#DCFCE7", color: "#15803D" }}>Indeks inti</span>}
                    </div>
                    {s.reverse_codes?.length > 0 && <div className="mt-2 text-xs" style={{ color: COLORS.textSoft }}>Reverse: <span className="font-mono">{s.reverse_codes.join(", ")}</span> · Penanda kualitas: {s.n_quality_flags}</div>}
                  </div>

                  <div className="rounded-comiq-sm border bg-white p-3" style={{ borderColor: COLORS.line }}>
                    <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLORS.textSoft }}>Contoh item</div>
                    <div className="mt-2 space-y-2">
                      {preview.sample_items?.map((it) => (
                        <div key={it.stable_item_code} className="text-sm" style={{ color: COLORS.ink }}>
                          <span className="font-mono font-bold">{it.stable_item_code}</span> · <span style={{ color: COLORS.textSoft }}>{it.anchor}</span>
                          <div className="text-sm">{it.wording}</div>
                          {it.options?.length > 0 && <div className="text-xs" style={{ color: COLORS.textSoft }}>{it.options.map((o) => o.label).join(" · ")}</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-comiq-sm border p-4" style={{ borderColor: COLORS.line }}>
                    <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#15803D" }}><CheckCircle2 size={15} /> Siap dibuat sebagai draf</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="import-mode-select">
                        <option value="new_instrument">Instrumen baru</option>
                        <option value="new_version" disabled={instruments.length === 0}>Versi baru dari instrumen yang ada</option>
                      </select>
                      {mode === "new_instrument" ? (
                        <>
                          <select value={targetStudy} onChange={(e) => setTargetStudy(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="import-study-select">
                            {studies.map((st) => <option key={st.study_id} value={st.study_id}>{st.title}</option>)}
                          </select>
                          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Judul instrumen" className={`${inputCls} flex-1`} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="import-title-input" />
                        </>
                      ) : (
                        <select value={targetInstrument} onChange={(e) => setTargetInstrument(e.target.value)} className={`${inputCls} flex-1`} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="import-instrument-select">
                          {instruments.map((inst) => <option key={inst.instrument_id} value={inst.instrument_id}>{inst.title}</option>)}
                        </select>
                      )}
                      <button onClick={createDraft} disabled={busy || (mode === "new_instrument" && !title.trim())} className="btn-comiq btn-accent disabled:opacity-40" data-testid="import-create-btn">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : null} Buat draf
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
